import { SelectionMode } from "@fluentui/react";
import React, { useEffect } from "react";
import {
    ClearFiltersButton,
    DatePicker,
    DropdownMultiselect,
    Grid,
    GridColumn,
    GridRow,
    ListTable,
    SearchBox,
    Stack
} from "sprint-common-web";

import { createFileRecordListingColumns } from "../../../file-records/listing/buttons/createFileRecordListingColumns";
import {
    FileRecordColumn,
    FileRecordListingConfig
} from "../../../file-records/listing/config/FileRecordListingConfig";
import { CloudFileDetails } from "../CloudFileDetails";
import { FileRecordLookupItem } from "./FileRecordLookupItem";
import { useFileRecordLookupListing } from "./hooks/useFileRecordListing";
import { useFileRecordLookupListingFilters } from "./hooks/useFileRecordLookupListingFilters";

type FileRecordLookupListingProps = {
    selectedItems: FileRecordLookupItem[];
    setSelectedItems: (items: FileRecordLookupItem[]) => void;
    cloudFile?: CloudFileDetails;
};

export const FileRecordLookupListing = ({
    cloudFile,
    setSelectedItems,
    selectedItems
}: FileRecordLookupListingProps): JSX.Element => {
    const {
        filters,
        setSort,
        setPage,
        setPageSize,
        setFilePath,
        setDeliveryFileName,
        setDeliveryRef,
        setStatuses,
        setContent,
        clearFilters,
        setAssociations,
        statusOptions,
        recordTypeOptions,
        associationsOptions,
        setRecordTypes,
        setCreatedDateFrom,
        setCreatedDateTo
    } = useFileRecordLookupListingFilters();
    const { areNotesLoading, isLoading, items, notes, reloadData, totalItems } = useFileRecordLookupListing();
    const [isFilteringInitialized, setIsFilteringInitialized] = React.useState(false);

    useEffect(() => {
        if (!isFilteringInitialized && cloudFile?.fileName) {
            setDeliveryFileName(cloudFile.fileName);
            setIsFilteringInitialized(true);
        }
    }, [cloudFile]);

    useEffect(() => {
        if (isFilteringInitialized) {
            reloadData(filters);
        }
    }, [filters, isFilteringInitialized]);

    const columns = createFileRecordListingColumns({
        config: getFileRecordLookupListingConfig(),
        notes,
        areNotesLoading,
        shortNotePreview: true,
        openInNewTab: true,
        linkAsSeparateColumn: true
    });

    return (
        <Grid>
            <GridRow>
                <GridColumn sm={2}>
                    <SearchBox
                        placeholder="Delivery file name"
                        onChange={setDeliveryFileName}
                        value={filters.deliveryFileName}
                        testId="file-records-filters-delivery-filename"
                    />
                </GridColumn>
                <GridColumn sm={2}>
                    <SearchBox
                        placeholder="File path"
                        onChange={setFilePath}
                        value={filters.filePath}
                        testId="file-records-filters-filepath"
                    />
                </GridColumn>
                <GridColumn sm={2}>
                    <SearchBox
                        placeholder="Content"
                        onChange={setContent}
                        value={filters.content}
                        testId="file-records-filters-content"
                    />
                </GridColumn>
                <GridColumn sm={2}>
                    <SearchBox
                        placeholder="Reference"
                        onChange={setDeliveryRef}
                        value={filters.deliveryRef}
                        testId="file-records-filters-reference"
                    />
                </GridColumn>

                <GridColumn sm={2}>
                    <Stack horizontalAlign="end">
                        <ClearFiltersButton isLabeled={true} onClear={clearFilters} />
                    </Stack>
                </GridColumn>
            </GridRow>

            <GridRow>
                <GridColumn sm={2}>
                    <DropdownMultiselect
                        placeholder="Status"
                        selectedKeys={filters.statuses}
                        onChange={setStatuses}
                        options={statusOptions}
                        testId="file-records-filters-status"
                    />
                </GridColumn>
                <GridColumn sm={2}>
                    <DropdownMultiselect
                        placeholder="Record type"
                        selectedKeys={filters.recordTypes}
                        onChange={setRecordTypes}
                        options={recordTypeOptions}
                        testId="file-records-filters-record-type"
                    />
                </GridColumn>
                <GridColumn sm={2}>
                    <DropdownMultiselect
                        placeholder="Associations"
                        selectedKeys={filters.associations}
                        onChange={setAssociations}
                        options={associationsOptions}
                        testId="file-records-filters-associations"
                    />
                </GridColumn>
                <GridColumn sm={2}>
                    <DatePicker
                        testId="file-records-filters-created-date-from"
                        placeholder="Created date from"
                        value={filters.createdDateFrom}
                        onDateSelect={setCreatedDateFrom}
                    />
                </GridColumn>

                <GridColumn sm={2}>
                    <DatePicker
                        testId="file-records-filters-created-date-to"
                        placeholder="Created date to"
                        value={filters.createdDateTo}
                        onDateSelect={setCreatedDateTo}
                    />
                </GridColumn>
            </GridRow>

            <GridRow>
                <GridColumn sm={12} style={{ height: 400, overflowY: "auto" }}>
                    <ListTable
                        columns={columns}
                        items={items}
                        selectedItems={selectedItems}
                        onSelectionChange={setSelectedItems}
                        selectionMode={SelectionMode.single}
                        pagination={{
                            page: filters.page,
                            pageSize: filters.pageSize,
                            totalItems,
                            onPageChange: setPage,
                            onPageSizeChange: setPageSize
                        }}
                        sort={{ by: filters.sortBy, order: filters.sortOrder }}
                        onSortChange={setSort}
                        isLoading={isLoading}
                        testId="file-records-lookup-listing-table"
                        noItemsMessage="Could not match any file records with the provided filters."
                    />
                </GridColumn>
            </GridRow>
        </Grid>
    );
};

export const getFileRecordLookupListingConfig = (): FileRecordListingConfig => ({
    pageSize: 10,
    columns: {
        [FileRecordColumn.DeliveryFileName]: { order: 1, visible: true },
        [FileRecordColumn.Link]: { order: 2, visible: true },
        [FileRecordColumn.FilePath]: { order: 3, visible: true },
        [FileRecordColumn.Content]: { order: 4, visible: true },
        [FileRecordColumn.RecordType]: { order: 5, visible: true },
        [FileRecordColumn.Info]: { order: 6, visible: true },
        [FileRecordColumn.Reference]: { order: 7, visible: true },
        [FileRecordColumn.Details]: { order: 8, visible: true },
        [FileRecordColumn.BuzzNotifications]: { order: 9, visible: false },
        [FileRecordColumn.Status]: { order: 10, visible: true },
        [FileRecordColumn.StatusDetails]: { order: 11, visible: true },
        [FileRecordColumn.CreateDate]: { order: 12, visible: true },
        [FileRecordColumn.UpdateDate]: { order: 13, visible: true },
        [FileRecordColumn.Notes]: { order: 14, visible: true },
        [FileRecordColumn.Actions]: { order: 15, visible: false }
    }
});
